import { Shimmer, TextTooltip } from '@components'
import { KeyFigure, KeyFigureSparkPoint, SPARK_MONTH_COUNT } from '../hooks/useKeyFigures'
import { formatCurrency } from '@utils'
import dayjs from 'dayjs'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { FaArrowTrendDown, FaChartLine, FaRightLeft } from 'react-icons/fa6'
import styled, { keyframes } from 'styled-components'
import { AskLuca } from './shared/AskLuca'
import { CardSurface } from './shared/Card'
import { WHEN_NARROW } from '../layout'

const ICONS: Record<KeyFigure['key'], IconType> = {
  revenue: FaRightLeft,
  profit: FaChartLine,
  expenses: FaArrowTrendDown
}

const SPARK_SHIMMER_HEIGHTS = [38, 52, 30, 64, 46, 72, 40, 58, 44, 50].slice(0, SPARK_MONTH_COUNT)

interface Props {
  figure: KeyFigure
  isLoading?: boolean
}

export const KeyFigureCard: React.FC<Props> = ({ figure, isLoading = false }) => {
  const [t] = useTranslation()
  const Icon = ICONS[figure.key]
  const peakValue = figure.peak?.value ?? 0

  // Trail is measured from zero, not the floor, so a down month hangs below.
  const sparkMax = figure.spark.reduce((max, point) => Math.max(max, point.value), 0)
  const sparkMin = figure.spark.reduce((min, point) => Math.min(min, point.value), 0)
  const sparkRange = sparkMax - sparkMin || 1
  const zeroLine = (-sparkMin / sparkRange) * 100

  const changeLabel =
    figure.changeRatio === null
      ? t('dashboard.keyFigureCard.noComparison')
      : t('dashboard.keyFigureCard.change', {
          change: `${figure.changeRatio >= 0 ? '+' : ''}${(figure.changeRatio * 100).toLocaleString(
            'fi-FI',
            { maximumFractionDigits: 1 }
          )} %`
        })

  return (
    <Surface>
      <TitleRow>
        <IconKnob>
          <Icon />
        </IconKnob>
        <Label>
          {t(
            figure.isYearToDate
              ? `dashboard.keyFigureCard.label.${figure.key}`
              : `dashboard.keyFigureCard.labelRanged.${figure.key}`
          )}
        </Label>
        <AskLuca
          prompts={
            t(`dashboard.keyFigureCard.ask.${figure.key}.prompts`, {
              returnObjects: true
            }) as string[]
          }
        />
      </TitleRow>

      {isLoading ? (
        <>
          <Shimmer width="8.5rem" height="1.75rem" borderRadius="0.25rem" />
          <Shimmer width="11rem" height="0.75rem" borderRadius="0.25rem" />
        </>
      ) : (
        <>
          <Value>{formatCurrency(figure.value)}</Value>
          <Change>{changeLabel}</Change>
        </>
      )}

      <Spark>
        {isLoading &&
          SPARK_SHIMMER_HEIGHTS.map((height, index) => (
            <SparkBarSlot key={index}>
              <Shimmer width="100%" height={`${height}%`} borderRadius="3px" />
            </SparkBarSlot>
          ))}
        {!isLoading && <SparkBaseline $zeroLine={zeroLine} />}
        {!isLoading &&
          figure.spark.map((point, index) => (
            <TextTooltip
              key={point.date}
              placement="top"
              openDelay={0}
              closeDelay={0}
              tooltip={`${formatMonth(point)} · ${formatCurrency(point.value)}`}
            >
              <SparkBar
                $index={index}
                $height={Math.max(
                  point.value === 0 ? 0 : 4,
                  (Math.abs(point.value) / sparkRange) * 100
                )}
                $zeroLine={zeroLine}
                $negative={point.value < 0}
                $peak={sparkMax > 0 && point.value === sparkMax}
              />
            </TextTooltip>
          ))}
      </Spark>

      <Footer>
        {isLoading ? (
          <>
            <Shimmer width="9rem" height="0.6875rem" borderRadius="0.25rem" />
            <Shimmer width="3.5rem" height="0.6875rem" borderRadius="0.25rem" />
          </>
        ) : (
          <>
            <FooterLabel>
              {figure.peak
                ? t('dashboard.keyFigureCard.peakMonth', { month: formatMonth(figure.peak) })
                : ''}
            </FooterLabel>
            <FooterValue>{figure.peak ? formatCurrency(peakValue) : ''}</FooterValue>
          </>
        )}
      </Footer>
    </Surface>
  )
}

const formatMonth = (point: KeyFigureSparkPoint) => dayjs(point.date).format('MMM YYYY')

const Surface = styled(CardSurface)`
  flex: 1;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;

  ${WHEN_NARROW} {
    padding: ${({ theme }) => theme.spacing.md}rem;
  }
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin: ${({ theme }) => `-${theme.spacing.lg}rem -${theme.spacing.lg}rem 0`};
  padding: ${({ theme }) => theme.spacing.lg}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};

  ${WHEN_NARROW} {
    margin: ${({ theme }) => `-${theme.spacing.md}rem -${theme.spacing.md}rem 0`};
    padding: ${({ theme }) => theme.spacing.md}rem;
  }
`

const IconKnob = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;

  ${WHEN_NARROW} {
    width: 1.25rem;
    height: 1.25rem;
    font-size: ${({ theme }) => theme.fontSize.px11}rem;
  }
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const Label = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.dashboard.textSubtle};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Value = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xxl}rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${WHEN_NARROW} {
    font-size: ${({ theme }) => theme.fontSize.lg}rem;
  }
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.menuBg};
  font-variant-numeric: tabular-nums;
`

const Change = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};

  ${WHEN_NARROW} {
    font-size: ${({ theme }) => theme.fontSize.px10}rem;
  }
`

const SparkBarSlot = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  align-items: flex-end;
  min-width: 0;
  height: 100%;
`

const Spark = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 3px;
  flex: 1;
  min-height: 2.125rem;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;

  /* Tooltip wraps each bar in inline-flex; position the bar against zero inside it. */
  > span {
    position: relative;
    flex: 1;
    align-items: flex-end;
    height: 100%;
  }
`

/* Div, not span: the wrapper rule above would otherwise restyle this line. */
const SparkBaseline = styled.div<{ $zeroLine: number }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${({ $zeroLine }) => $zeroLine}%;
  height: 1px;
  background-image: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.borderSoft} 55%,
    transparent 55%
  );
  background-size: 6px 1px;
`

const sparkOpen = keyframes`
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
`

const SparkBar = styled.span<{
  $index: number
  $height: number
  $zeroLine: number
  $negative: boolean
  $peak: boolean
}>`
  display: block;
  position: absolute;
  left: 0;
  width: 100%;
  height: ${({ $height }) => $height}%;
  border-radius: 3px;
  transform-origin: ${({ $negative }) => ($negative ? 'top' : 'bottom')};
  animation: ${sparkOpen} 0.55s ease-out both;
  animation-delay: ${({ $index }) => $index * 0.04}s;
  ${({ $negative, $zeroLine, $height }) =>
    $negative ? `top: ${100 - $zeroLine}%; height: ${$height}%;` : `bottom: ${$zeroLine}%;`}
  background: ${({ theme, $negative, $peak }) =>
    $negative ? theme.dashboard.critical : $peak ? theme.colors.primary : theme.dashboard.infoSoft};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Footer = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const FooterLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FooterValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`
